/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  CAPACITY_DATA_LOAD,
  CAPACITY_DATA_LOAD_FAIL,
  CAPACITY_DATA_LOAD_SUCCESS,
  DEMOGRAPHICS_FILTER,
  DEMOGRAPHICS_FILTER_COUNTRY_VALUE_REMOVE,
  DEMOGRAPHICS_FILTER_FAIL,
  DEMOGRAPHICS_FILTER_REGION_VALUE_REMOVE,
  DEMOGRAPHICS_FILTER_RESET,
  DEMOGRAPHICS_FILTER_SUCCESS,
  DEMOGRAPHICS_FILTER_VALUE_ADD,
  DEMOGRAPHICS_FILTER_VALUE_REMOVE,
  DEMOGRAPHICS_FILTER_VALUE_UPDATE,
} from "Constants/ActionTypes";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { ICapDataResult } from "Interfaces/Demographics/ICapDataResult";
import { IDemographicsResult } from "Interfaces/Demographics/IDemographicsResult";
import { IFilterValue } from "Interfaces/IFilterValue";

/**
 * Action for when adding selected filter value.
 * @interface
 */
export interface IDemographicsFilterValueAdd {
  type: typeof DEMOGRAPHICS_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter value.
 * @interface
 */
export interface IDemographicsFilterValueRemove {
  type: typeof DEMOGRAPHICS_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter cl region value.
 * @interface
 */
export interface IDemographicsFilterRegionValueRemove {
  type: typeof DEMOGRAPHICS_FILTER_REGION_VALUE_REMOVE;
}

/**
 * Action for when removing selected filter country value.
 * @interface
 */
export interface IDemographicsFilterCountryValueRemove {
  type: typeof DEMOGRAPHICS_FILTER_COUNTRY_VALUE_REMOVE;
}

/**
 * Action for when updating selected filter value.
 * @interface
 */
export interface IDemographicsFilterValueUpdate {
  type: typeof DEMOGRAPHICS_FILTER_VALUE_UPDATE;
  filterValue: IFilterValue;
}

/**
 * Action for when filtering demographics.
 * @interface
 */
export interface IFilterDemographics {
  type: typeof DEMOGRAPHICS_FILTER;
  applicationType: string;
  selectedDemographicsFilterValues: IFilterValue[];
  tenant: TenantTypesEnum;
}

/**
 * Action for when filter demographics succeeds.
 * @interface
 */
export interface IFilterDemographicsSuccess {
  type: typeof DEMOGRAPHICS_FILTER_SUCCESS;
  demographics: IDemographicsResult;
}

/**
 * Action for when filter demographics failed.
 * @interface
 */
export interface IFilterDemographicsFail {
  type: typeof DEMOGRAPHICS_FILTER_FAIL;
  error: string;
}

/**
 * Action for when we reset the demographics filter.
 */
export interface IResetDemographicsFilter {
  type: typeof DEMOGRAPHICS_FILTER_RESET;
}

/**
 * Action for loading capacity data.
 */
export interface ICapDataLoad {
  type: typeof CAPACITY_DATA_LOAD;
}

/**
 * Action for when loading capacity data succeeds.
 * @interface
 */
export interface ICapDataLoadSuccess {
  type: typeof CAPACITY_DATA_LOAD_SUCCESS;
  capData: ICapDataResult;
}

/**
 * Action for when loading capacity data fails.
 * @interface
 */
export interface ICapDataLoadFail {
  type: typeof CAPACITY_DATA_LOAD_FAIL;
  error: string;
}

/**
 * Action creator for DEMOGRAPHICS_FILTER_VALUE_ADD.
 * @param filterValue filter value to add.
 */
export function addDemographicsFilterValue(filterValue: IFilterValue): DemographicActionTypes {
  return {
    type: DEMOGRAPHICS_FILTER_VALUE_ADD,
    filterValue,
  };
}

/**
 * Action creator for DEMOGRAPHICS_FILTER_VALUE_REMOVE.
 * @param filterValue filter value to remove.
 */
export function removeDemographicsFilterValue(filterValue: IFilterValue): DemographicActionTypes {
  return {
    type: DEMOGRAPHICS_FILTER_VALUE_REMOVE,
    filterValue,
  };
}

/**
 * Action creator for DEMOGRAPHICS_FILTER_REGION_VALUE_REMOVE.
 */
export function removeDemographicsFilterRegionValue(): DemographicActionTypes {
  return {
    type: DEMOGRAPHICS_FILTER_REGION_VALUE_REMOVE,
  };
}

/**
 * Action creator for DEMOGRAPHICS_FILTER_COUNTRY_VALUE_REMOVE.
 */
export function removeDemographicsFilterCountryValue(): DemographicActionTypes {
  return {
    type: DEMOGRAPHICS_FILTER_COUNTRY_VALUE_REMOVE,
  };
}

/**
 * Action creator for DEMOGRAPHICS_FILTER_VALUE_UPDATE.
 * @param filterValue filter value to update.
 */
export function updateDemographicsFilterValue(filterValue: IFilterValue): DemographicActionTypes {
  return {
    type: DEMOGRAPHICS_FILTER_VALUE_UPDATE,
    filterValue,
  };
}

/**
 * Action creator for DEMOGRAPHICS_FILTER.
 */
export function filterDemographics(
  selectedDemographicsFilterValues: IFilterValue[],
  applicationType: string,
  tenant: TenantTypesEnum
): DemographicActionTypes {
  return {
    type: DEMOGRAPHICS_FILTER,
    selectedDemographicsFilterValues,
    applicationType,
    tenant,
  };
}

/**
 * Action creator for DEMOGRAPHICS_FILTER_SUCCESS.
 * @param demographics the successful filtered demographics.
 */
export function filterDemographicsSuccess(
  demographics: IDemographicsResult
): DemographicActionTypes {
  return {
    type: DEMOGRAPHICS_FILTER_SUCCESS,
    demographics,
  };
}

/**
 * Action creator for DEMOGRAPHICS_FILTER_FAIL.
 * @param error the error message from the API.
 */
export function filterDemographicsFail(error: string): DemographicActionTypes {
  return {
    type: DEMOGRAPHICS_FILTER_FAIL,
    error,
  };
}

/**
 * Action creator for DEMOGRAPHICS_FILTER_RESET.
 */
export function resetDemographics(): IResetDemographicsFilter {
  return {
    type: DEMOGRAPHICS_FILTER_RESET,
  };
}

/**
 * Action creator for CAPACITY_DATA_LOAD.
 */
export function capDataLoad(): DemographicActionTypes {
  return {
    type: CAPACITY_DATA_LOAD,
  };
}

/**
 * Action creator for CAPACITY_DATA_LOAD_SUCCESS.
 * @param capData the capacity data successfully returned.
 */
export function capDataLoadSuccess(capData: ICapDataResult): DemographicActionTypes {
  return {
    type: CAPACITY_DATA_LOAD_SUCCESS,
    capData,
  };
}

/**
 * Action creator for CAPACITY_LOAD_FAIL.
 * @param error the error message from the API.
 */
export function capDataLoadFail(error: string): DemographicActionTypes {
  return {
    type: CAPACITY_DATA_LOAD_FAIL,
    error,
  };
}

export type DemographicActionTypes =
  | IDemographicsFilterCountryValueRemove
  | IDemographicsFilterRegionValueRemove
  | IDemographicsFilterValueAdd
  | IDemographicsFilterValueRemove
  | IDemographicsFilterValueUpdate
  | IResetDemographicsFilter
  | IFilterDemographics
  | IFilterDemographicsFail
  | IFilterDemographicsSuccess
  | ICapDataLoad
  | ICapDataLoadSuccess
  | ICapDataLoadFail;
